import { zodResolver } from "@hookform/resolvers/zod";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  TUTORIAL_SECTION,
  type TutorialStep,
  tutorialStepFormSchema,
  type TutorialStepFormValues,
} from "../types";

const TUTORIAL_SECTION_LABELS: Record<string, string> = {
  [TUTORIAL_SECTION.NAVIGATION]: "Navigation",
  [TUTORIAL_SECTION.TIMECLOCK_USE]: "Timeclock use",
};

const TUTORIAL_SECTION_OPTIONS = Object.entries(TUTORIAL_SECTION_LABELS).map(([value, label]) => ({
  value,
  label,
}));

function ImageThumbnail({ src }: { src?: string }) {
  return (
    <Box
      sx={(theme) => ({
        width: 64,
        height: 64,
        backgroundColor: theme.palette.grey[200],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 1,
        overflow: "hidden",
        flexShrink: 0,
      })}
    >
      {src ? (
        <img src={src} alt="Step" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <ImageIcon color="disabled" />
      )}
    </Box>
  );
}

interface TutorialStepEditorProps {
  step: TutorialStep;
  stepIndex: number;
  isEditing: boolean;
  isSaving?: boolean;
  canDelete?: boolean;
  onSave: (step: TutorialStep) => void;
  onDelete: () => void;
  onEdit: () => void;
  onCancel: () => void;
}

export function TutorialStepEditor(props: TutorialStepEditorProps) {
  const {
    step,
    stepIndex,
    isEditing,
    isSaving = false,
    canDelete = true,
    onSave,
    onDelete,
    onEdit,
    onCancel,
  } = props;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = useForm<TutorialStepFormValues>({
    resolver: zodResolver(tutorialStepFormSchema),
    defaultValues: step,
    mode: "onChange",
  });

  const watchedImage = watch("tutorialImage");

  useEffect(() => {
    reset(step);
  }, [step, isEditing, reset]);

  function onFormSubmit(data: TutorialStepFormValues) {
    onSave(data);
  }

  if (isEditing) {
    return (
      <Paper variant="outlined" sx={{ padding: 2, marginBottom: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Text variant="body1">Step {stepIndex + 1}</Text>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={isSaving || !isValid}
                onClick={handleSubmit(onFormSubmit)}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
              <Button variant="text" size="small" disabled={isSaving} onClick={onCancel}>
                Cancel
              </Button>
              <IconButton
                size="small"
                color="error"
                disabled={isSaving || !canDelete}
                onClick={onDelete}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Text variant="body1" sx={{ minWidth: 60, paddingTop: 1 }}>
              Heading
            </Text>
            <Controller
              name="tutorialHeadline"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  placeholder="Enter step heading"
                  error={Boolean(errors.tutorialHeadline)}
                  helperText={errors.tutorialHeadline?.message}
                  disabled={isSaving}
                />
              )}
            />
          </Stack>

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Text variant="body1" sx={{ minWidth: 60, paddingTop: 1 }}>
              Body
            </Text>
            <Controller
              name="tutorialBody"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  size="small"
                  rows={2}
                  placeholder="Enter step description"
                  error={Boolean(errors.tutorialBody)}
                  helperText={errors.tutorialBody?.message}
                  disabled={isSaving}
                />
              )}
            />
          </Stack>

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Text variant="body1" sx={{ minWidth: 60, paddingTop: 1 }}>
              Image
            </Text>
            <ImageThumbnail src={watchedImage} />
            <Controller
              name="tutorialImage"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  placeholder="Enter image URL"
                  error={Boolean(errors.tutorialImage)}
                  helperText={errors.tutorialImage?.message}
                  disabled={isSaving}
                />
              )}
            />
          </Stack>

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Text variant="body1" sx={{ minWidth: 60, paddingTop: 1 }}>
              Section
            </Text>
            <FormControl fullWidth size="small">
              <Controller
                name="tutorialSection"
                control={control}
                render={({ field }) => (
                  <Select {...field} disabled={isSaving}>
                    {TUTORIAL_SECTION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ padding: 2, marginBottom: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text variant="body1">Step {stepIndex + 1}</Text>
          <Stack direction="row" spacing={1}>
            <Button variant="text" size="small" onClick={onEdit}>
              Edit
            </Button>
            <IconButton size="small" color="error" disabled={!canDelete} onClick={onDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Text variant="body1">{step.tutorialHeadline || "No heading"}</Text>
        <Text variant="body2">{step.tutorialBody || "No description"}</Text>
        <Text variant="body2" color="text.secondary">
          Section: {TUTORIAL_SECTION_LABELS[step.tutorialSection] ?? step.tutorialSection}
        </Text>

        <ImageThumbnail src={step.tutorialImage} />
      </Stack>
    </Paper>
  );
}
